import { useState, useEffect } from 'react';
import { User, Save, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

type EditableFields = {
  date_naissance: string;
  lieu_naissance: string;
  ine: string;
  sexe: 'M' | 'F' | 'X' | '';
};

type ReadOnlyInfo = {
  first_name: string;
  last_name: string;
  email: string;
  numero_candidat: string | null;
  session_examen: number | null;
  langue_vivante_1: string | null;
  class_name: string | null;
};

const StudentProfile = () => {
  const { user } = useAuth();
  const [studentId, setStudentId] = useState<string | null>(null);
  const [readOnly, setReadOnly] = useState<ReadOnlyInfo | null>(null);
  const [form, setForm] = useState<EditableFields>({
    date_naissance: '',
    lieu_naissance: '',
    ine: '',
    sexe: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof EditableFields, string>>>({});

  useEffect(() => {
    if (!user?.email) return;
    fetchProfile();
  }, [user]);

  const fetchProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('students')
        .select(`
          id,
          first_name,
          last_name,
          email,
          numero_candidat,
          session_examen,
          langue_vivante_1,
          date_naissance,
          lieu_naissance,
          ine,
          sexe,
          class:classes(name)
        `)
        .eq('email', user!.email)
        .single();

      if (error) throw error;

      setStudentId(data.id);
      setReadOnly({
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        numero_candidat: data.numero_candidat,
        session_examen: data.session_examen,
        langue_vivante_1: data.langue_vivante_1,
        class_name: (data.class as { name: string } | null)?.name ?? null,
      });
      setForm({
        date_naissance: data.date_naissance ?? '',
        lieu_naissance: data.lieu_naissance ?? '',
        ine: data.ine ?? '',
        sexe: (data.sexe as 'M' | 'F' | 'X' | null) ?? '',
      });
    } catch (err) {
      console.error('Error fetching student profile:', err);
      setMessage({ type: 'error', text: 'Impossible de charger votre profil.' });
    } finally {
      setIsLoading(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    if (form.date_naissance) {
      const date = new Date(form.date_naissance);
      const now = new Date();
      if (isNaN(date.getTime())) newErrors.date_naissance = 'Date invalide';
      else if (date > now) newErrors.date_naissance = 'La date ne peut pas être dans le futur';
      else if (date < new Date('1950-01-01')) newErrors.date_naissance = 'Date trop ancienne';
    }

    if (form.ine) {
      const trimmed = form.ine.trim().toUpperCase();
      if (!/^[0-9A-Z]{11}$/.test(trimmed)) {
        newErrors.ine = "L'INE doit comporter 11 caractères (chiffres et lettres uniquement)";
      }
    }

    if (form.lieu_naissance && form.lieu_naissance.length > 100) {
      newErrors.lieu_naissance = 'Maximum 100 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!studentId) return;
    setMessage(null);

    if (!validate()) return;

    setIsSaving(true);
    try {
      const payload: Record<string, string | null> = {
        date_naissance: form.date_naissance || null,
        lieu_naissance: form.lieu_naissance.trim() || null,
        ine: form.ine ? form.ine.trim().toUpperCase() : null,
        sexe: form.sexe || null,
      };

      const { error } = await supabase
        .from('students')
        .update(payload)
        .eq('id', studentId);

      if (error) throw error;

      setMessage({ type: 'success', text: 'Vos informations ont bien été enregistrées.' });
    } catch (err) {
      console.error('Error updating profile:', err);
      const msg = err instanceof Error ? err.message : 'Erreur inconnue';
      setMessage({ type: 'error', text: `Échec de l'enregistrement : ${msg}` });
    } finally {
      setIsSaving(false);
    }
  };

  const completenessCount = [
    form.date_naissance,
    form.lieu_naissance,
    form.ine,
    form.sexe,
  ].filter(Boolean).length;
  const completenessPercent = Math.round((completenessCount / 4) * 100);

  if (isLoading) {
    return (
      <div className="p-8 text-center text-gray-500">Chargement de votre profil...</div>
    );
  }

  return (
    <div className="animate-fade-in max-w-3xl mx-auto">
      <header className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="bg-primary-100 p-3 rounded-full">
            <User className="h-6 w-6 text-primary-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Mon profil</h1>
        </div>
        <p className="text-gray-600">
          Renseignez les informations nécessaires à la génération de votre livret scolaire officiel BTS.
        </p>
      </header>

      {/* Completeness indicator */}
      <div className="mb-6 bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Complétude du profil livret
          </span>
          <span
            className={`text-sm font-semibold ${
              completenessPercent === 100 ? 'text-success-600' : 'text-amber-600'
            }`}
          >
            {completenessCount}/4 champs ({completenessPercent}%)
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${
              completenessPercent === 100 ? 'bg-success-500' : 'bg-amber-500'
            }`}
            style={{ width: `${completenessPercent}%` }}
          />
        </div>
        {completenessPercent < 100 && (
          <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
            <Info className="h-3 w-3" />
            Ces informations sont nécessaires pour la génération de votre livret scolaire officiel.
          </p>
        )}
      </div>

      {/* Flash message */}
      {message && (
        <div
          className={`mb-6 rounded-lg p-4 flex items-start gap-3 ${
            message.type === 'success'
              ? 'bg-success-50 text-success-800 border border-success-200'
              : 'bg-error-50 text-error-800 border border-error-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          )}
          <span className="text-sm">{message.text}</span>
        </div>
      )}

      {/* Read-only admin info */}
      {readOnly && (
        <section className="mb-6 bg-gray-50 rounded-lg p-6 border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Informations administratives
          </h2>
          <p className="text-xs text-gray-500 mb-4 flex items-center gap-1">
            <Info className="h-3 w-3" />
            Ces informations sont gérées par l'administration. Contactez-la pour toute correction.
          </p>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-gray-500">Nom</dt>
              <dd className="font-medium text-gray-900">{readOnly.last_name}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Prénom</dt>
              <dd className="font-medium text-gray-900">{readOnly.first_name}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Email</dt>
              <dd className="font-medium text-gray-900">{readOnly.email}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Classe</dt>
              <dd className="font-medium text-gray-900">{readOnly.class_name ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Numéro candidat</dt>
              <dd className="font-medium text-gray-900">{readOnly.numero_candidat ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Session d'examen</dt>
              <dd className="font-medium text-gray-900">{readOnly.session_examen ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Langue vivante 1</dt>
              <dd className="font-medium text-gray-900">{readOnly.langue_vivante_1 ?? '—'}</dd>
            </div>
          </dl>
        </section>
      )}

      {/* Editable form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Informations personnelles
        </h2>
        <p className="text-xs text-gray-500 mb-6">
          Renseignez ces informations avec soin : elles figureront sur votre livret scolaire officiel.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Date de naissance */}
          <div>
            <label
              htmlFor="date_naissance"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Date de naissance
            </label>
            <input
              id="date_naissance"
              type="date"
              value={form.date_naissance}
              onChange={(e) =>
                setForm({ ...form, date_naissance: e.target.value })
              }
              className="input-field w-full"
              max={new Date().toISOString().split('T')[0]}
            />
            {errors.date_naissance && (
              <p className="text-xs text-error-600 mt-1">{errors.date_naissance}</p>
            )}
          </div>

          {/* Sexe */}
          <div>
            <label
              htmlFor="sexe"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Sexe
            </label>
            <select
              id="sexe"
              value={form.sexe}
              onChange={(e) =>
                setForm({ ...form, sexe: e.target.value as 'M' | 'F' | 'X' | '' })
              }
              className="input-field w-full"
            >
              <option value="">— Non renseigné —</option>
              <option value="M">Masculin</option>
              <option value="F">Féminin</option>
              <option value="X">Non binaire / Autre</option>
            </select>
          </div>

          {/* Lieu de naissance */}
          <div className="md:col-span-2">
            <label
              htmlFor="lieu_naissance"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Lieu de naissance
            </label>
            <input
              id="lieu_naissance"
              type="text"
              value={form.lieu_naissance}
              onChange={(e) =>
                setForm({ ...form, lieu_naissance: e.target.value })
              }
              placeholder="Ex. PARIS, LIMERICK..."
              maxLength={100}
              className="input-field w-full"
            />
            {errors.lieu_naissance && (
              <p className="text-xs text-error-600 mt-1">{errors.lieu_naissance}</p>
            )}
          </div>

          {/* INE */}
          <div className="md:col-span-2">
            <label
              htmlFor="ine"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              INE (Identifiant National Étudiant)
            </label>
            <input
              id="ine"
              type="text"
              value={form.ine}
              onChange={(e) => setForm({ ...form, ine: e.target.value.toUpperCase() })}
              placeholder="11 caractères (chiffres + lettres)"
              maxLength={11}
              className="input-field w-full font-mono uppercase"
            />
            <p className="text-xs text-gray-500 mt-1">
              L'INE figure sur votre relevé de notes du baccalauréat ou sur votre carte d'étudiant.
            </p>
            {errors.ine && (
              <p className="text-xs text-error-600 mt-1">{errors.ine}</p>
            )}
          </div>
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-medium px-5 py-2.5 rounded-md shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4" />
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StudentProfile;
